'use client';

import { createContext, useContext, useEffect, useState } from 'react';
import { createClient } from '@/lib/supabase/client';
import type { User, Session } from '@supabase/supabase-js';

const ALLOWED_DOMAIN = '@vaschools.edu.vn';

interface UserProfile {
  id: string;
  email: string;
  full_name: string;
  avatar_url?: string;
  role: 'super_admin' | 'admin' | 'operator' | 'moderator' | 'user';
  is_active: boolean;
}

interface AuthContextType {
  user: User | null;
  session: Session | null;
  profile: UserProfile | null;
  loading: boolean;
  isGuest: boolean;
  enterGuestMode: () => void;
  exitGuestMode: () => void;
  signInWithGoogle: () => Promise<void>;
  signInWithWorkOS: () => Promise<void>;
  signInWithMagicLink: (email: string) => Promise<void>;
  signInWithPassword: (email: string, password: string) => Promise<void>;
  signUp: (email: string, password: string, fullName?: string) => Promise<void>;
  sendOtp: (email: string) => Promise<void>;
  verifyOtp: (email: string, token: string) => Promise<void>;
  signOut: () => Promise<void>;
  getUserProfile: () => Promise<UserProfile | null>;
}

const AuthContext = createContext<AuthContextType>({
  user: null,
  session: null,
  profile: null,
  loading: true,
  isGuest: false,
  enterGuestMode: () => {},
  exitGuestMode: () => {},
  signInWithGoogle: async () => {},
  signInWithWorkOS: async () => {},
  signInWithMagicLink: async () => {},
  signInWithPassword: async () => {},
  signUp: async () => {},
  sendOtp: async () => {},
  verifyOtp: async () => {},
  signOut: async () => {},
  getUserProfile: async () => null,
});

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within AuthProvider');
  return context;
};

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [isGuest, setIsGuest] = useState(false);
  const supabase = createClient();

  const fetchProfile = async (userId: string): Promise<UserProfile | null> => {
    try {
      const { data, error } = await supabase
        .from('user_profiles')
        .select('*')
        .eq('id', userId)
        .single();
      if (error) return null;
      return data as UserProfile;
    } catch {
      return null;
    }
  };

  useEffect(() => {
    supabase.auth.getSession().then(async ({ data: { session } }) => {
      setSession(session);
      setUser(session?.user ?? null);
      if (session?.user) {
        const p = await fetchProfile(session.user.id);
        setProfile(p);
      }
      setLoading(false);
    });

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange(async (_event, session) => {
      setSession(session);
      setUser(session?.user ?? null);
      if (session?.user) {
        const p = await fetchProfile(session.user.id);
        setProfile(p);
      } else {
        setProfile(null);
      }
      setLoading(false);
    });

    return () => subscription.unsubscribe();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const getAuthRedirectTo = () => {
    // Always production — PKCE cookies must be set on baomat.langnghe.me.
    const configured = process.env.NEXT_PUBLIC_SITE_URL?.replace(/\/$/, '') || '';
    const origin = typeof window !== 'undefined' ? window.location.origin : '';
    const base = configured || origin;
    return `${base}/auth/callback`;
  };

  const ensureProductionHost = () => {
    const configured = process.env.NEXT_PUBLIC_SITE_URL?.replace(/\/$/, '') || '';
    if (!configured || typeof window === 'undefined') return true;
    if (window.location.origin === configured) return true;
    window.location.assign(`${configured}/sign-up-login-screen`);
    return false;
  };

  const signInWithGoogle = async () => {
    if (!ensureProductionHost()) return;
    const { error } = await supabase.auth.signInWithOAuth({
      provider: 'google',
      options: {
        redirectTo: getAuthRedirectTo(),
        queryParams: {
          access_type: 'offline',
          prompt: 'select_account',
        },
      },
    });
    if (error) throw error;
  };

  const signInWithWorkOS = async () => {
    if (!ensureProductionHost()) return;
    const { error } = await supabase.auth.signInWithOAuth({
      provider: 'workos',
      options: {
        redirectTo: getAuthRedirectTo(),
      },
    });
    if (error) throw error;
  };

  const signInWithMagicLink = async (email: string) => {
    if (!email.endsWith(ALLOWED_DOMAIN)) {
      throw new Error(`Only ${ALLOWED_DOMAIN} accounts are permitted`);
    }
    const { error } = await supabase.auth.signInWithOtp({
      email,
      options: {
        emailRedirectTo: getAuthRedirectTo(),
        shouldCreateUser: true,
      },
    });
    if (error) throw error;
  };

  const signInWithPassword = async (email: string, password: string) => {
    const { error } = await supabase.auth.signInWithPassword({ email, password });
    if (error) throw error;
  };

  const signUp = async (email: string, password: string, fullName?: string) => {
    const { error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        data: {
          full_name: fullName || email.split('@')[0],
        },
      },
    });
    if (error) throw error;
  };

  const sendOtp = async (email: string) => {
    if (!email.endsWith(ALLOWED_DOMAIN)) {
      throw new Error(`Only ${ALLOWED_DOMAIN} accounts are permitted`);
    }
    const { error } = await supabase.auth.signInWithOtp({
      email,
      options: {
        shouldCreateUser: true,
      },
    });
    if (error) throw error;
  };

  const verifyOtp = async (email: string, token: string) => {
    const { error } = await supabase.auth.verifyOtp({
      email,
      token,
      type: 'email',
    });
    if (error) throw error;
  };

  const signOut = async () => {
    const { error } = await supabase.auth.signOut();
    if (error) throw error;
    setProfile(null);
    setIsGuest(false);
  };

  const getUserProfile = async (): Promise<UserProfile | null> => {
    if (!user) return null;
    return fetchProfile(user.id);
  };

  const enterGuestMode = () => {
    setIsGuest(true);
  };

  const exitGuestMode = () => {
    setIsGuest(false);
  };

  return (
    <AuthContext.Provider
      value={{ user, session, profile, loading, isGuest, enterGuestMode, exitGuestMode, signInWithGoogle, signInWithWorkOS, signInWithMagicLink, signInWithPassword, signUp, sendOtp, verifyOtp, signOut, getUserProfile }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
